import { paths } from '@/constants/paths'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { runExperimentsOnServer } from '@/experimentation/server'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { buildBrandedCloudinaryImageTransforms } from '@/layout/Seo/SeoHelper'
import { HideTopNavigationLayout } from '@/layouts/HideTopNavigationLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { TellYourStoryPromptProvider } from '@/organisms/TellYourStoryPrompt'
import { Captcha } from '@/services/Captcha'
import { createGuildDeepLink } from '@/services/DeepLinkService'
import { getOfferById, Offer } from '@/services/PifService'
import { getProjectMeta, ProjectMeta } from '@/services/ProjectsService'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PayItForwardProjectCheckoutOfferView } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView'
import { PayItForwardCheckoutProvider } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/usePayItForwardState/usePayItForwardState'

interface PayItForwardProjectCheckoutOfferPageParams {
  cloudinaryImageTransformation: string
  locale: string
  offer: Offer
  guildDeepLink: string | null
  projectMeta: ProjectMeta
  pifNameInCreditsEnabled?: boolean
}

export const PayItForwardProjectCheckoutOfferPage: NextPageWithLayout<PayItForwardProjectCheckoutOfferPageParams> = ({
  cloudinaryImageTransformation,
  locale,
  offer,
  guildDeepLink,
  projectMeta,
  pifNameInCreditsEnabled,
}) => {
  const { t } = useTranslate('pif')
  const projectName = projectMeta.name
  return (
    <div className="bg-white">
      <Captcha />
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: projectMeta.discoveryPosterLandscapeCloudinaryPath,
          cloudinaryTransformation: cloudinaryImageTransformation,
          description:
            projectMeta.pifExplainerText ||
            t(
              'payItForwardForProjectDescriptionv2',
              'Pay it Forward for {{projectName}} on Angel and help support stories you love, enabling others to experience them too',
              {
                projectName,
              },
            ),
          locale,
          path: `${paths.payItForward.index}/${projectMeta.slug}/checkout/${offer.id}`,
          title: t('payItForwardForProjectAngel', 'Pay it Forward for {{projectName}} on Angel', { projectName }),
        })}
      />
      <ProjectMetaProvider project={projectMeta}>
        <TellYourStoryPromptProvider projectSlug={projectMeta.slug} projectName={projectMeta.name}>
          <PayItForwardCheckoutProvider>
            <MarketingVideoPopup />
            <PayItForwardProjectCheckoutOfferView
              projectSlug={projectMeta.slug}
              offer={offer}
              name={projectMeta.name}
              guildDeepLink={guildDeepLink || ''}
              pifNameInCreditsEnabled={pifNameInCreditsEnabled}
            />
          </PayItForwardCheckoutProvider>
        </TellYourStoryPromptProvider>
      </ProjectMetaProvider>
    </div>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<PayItForwardProjectCheckoutOfferPageParams>(
  async ({ federationClient, locale, params, preview, req, res, next404 }) => {
    const projectSlug = getItemOrFirstEntry(params.projectSlug)
    const offerId = getItemOrFirstEntry(params.offerId)
    if (!projectSlug || !offerId) return next404()

    const [offer, projectMeta, guildDeepLink, translations] = await Promise.all([
      getOfferById({ projectSlug, offerId }, federationClient),
      getProjectMeta({ slug: projectSlug }, federationClient, { locale, preview }),
      createGuildDeepLink(
        {
          route: 'Guild',
          path: '/home#download',
        },
        {
          campaign: 'guild-web',
          feature: 'pif-guild-upsell',
          channel: 'angel-web',
        },
      ),
      loadTranslations(locale, ['common', 'pif', 'account']),
    ])
    if (!offer || !projectMeta) return next404()

    const cloudinaryImageTransformation = buildBrandedCloudinaryImageTransforms({
      logoCloudinaryPath: projectMeta.logoCloudinaryPath,
    })

    const experiments = await runExperimentsOnServer(req, res, [
      { experimentKey: 'pif-name-in-credits', defaultValue: false },
    ])
    const pifNameInCreditsEnabled = Boolean(experiments?.['pif-name-in-credits'])

    return {
      props: {
        guildDeepLink,
        locale,
        projectMeta,
        offer,
        cloudinaryImageTransformation,
        pifNameInCreditsEnabled,
        ...translations,
      },
    }
  },
)

PayItForwardProjectCheckoutOfferPage.getLayout = (page) => {
  return <HideTopNavigationLayout isDarkMode={false}>{page}</HideTopNavigationLayout>
}

export default PayItForwardProjectCheckoutOfferPage
